import React, { useEffect } from 'react';
import {
  Card,
  CardContent,
  Direction,
  IconButton,
  useTheme,
} from '@mui/material';
import styles from './ccf-customer-history-card.module.scss';
import { CustomerHistory } from '../../model/customer-history';
import {
  CcfBox,
  CcfPhoneInboundIcon,
  CcfPhoneOutboundIcon,
  CcfDigitalIcon,
  CcfEmailIcon,
  CcfPersonIcon,
  CcfTypography,
} from '@nice-ccf/ui-controls';
import {
  updateContactInteraction,
  getApplicationDirection,
  interactionSliceActions,
} from '@nice-ccf/ui-components';
import { useSelector, useDispatch } from 'react-redux';
import { getInteractionData } from '../../../ccf-interaction-workspace/ccf-interaction-mock-data';

export interface CcfCustomerHistoryCardProps {
  interactionId: any;
  cardInfo: CustomerHistory;
  index: number;
}

/**
 * CcfCustomerHistoryCard
 * @param props -CcfCustomerHistoryCardProps
 * @example - `<CcfCustomerHistoryCard cardInfo=CustomerHistory index=number />`
 */
export function CcfCustomerHistoryCard(props: CcfCustomerHistoryCardProps) {
  const theme = useTheme();
  const appDirection: Direction = useSelector(getApplicationDirection);
  const dispatch = useDispatch();
  let correspondingDate: any = '';
  const { setEmailInteractionDates } = interactionSliceActions;

  useEffect(() => {
    //Added this use effect as we are not calling returnPreviousDate() when status is Open
    //So it is causing the issue while displaying date on email header
    correspondingDate = returnPreviousDate();
  }, [props?.cardInfo.contactStatus === 'Open']);
  /**
   * channelIconClasses used to add classes based on status and position
   * @param cardStatus -?- active or not active states
   * @param position -?- right or left
   * @example  channelIconClasses();
   */
  const channelIconClasses = (cardStatus = '', position = '') => {
    if (position === 'right' && cardStatus === 'Open') {
      return styles.activeStatusIcon;
    } else if (
      position === 'right' &&
      (cardStatus === 'Closed' || cardStatus === 'Escalated')
    ) {
      return styles.inactiveStatusIcon;
    } else {
      return styles.titleIcon;
    }
  };

  /**
   * cardStatusIcon used to render the channel icon based on channel type and postion
   * @param card -CustomerHistory
   * @param position - left or right
   * @example - cardStatusIcon(card, position)
   */
  const cardStatusIcon = (card: CustomerHistory, position = 'left') => {
    const channel = card.digitalChannel;
    switch (channel) {
      case 'voice':
        if (card.isOutbound) {
          return (
            <CcfPhoneOutboundIcon
              className={channelIconClasses(card.contactStatus, position)}
            />
          );
        } else {
          return (
            <CcfPhoneInboundIcon
              className={channelIconClasses(card.contactStatus, position)}
            />
          );
        }
      case 'email':
        return (
          <CcfEmailIcon
            className={channelIconClasses(card.contactStatus, position)}
          />
        );
      case 'digital':
        return (
          <CcfDigitalIcon
            className={channelIconClasses(card.contactStatus, position)}
          />
        );
      case 'customer':
        return (
          <CcfPersonIcon
            className={channelIconClasses(card.contactStatus, position)}
          />
        );
      default:
        return (
          <CcfPersonIcon
            className={channelIconClasses(card.contactStatus, position)}
          />
        );
    }
  };

  /**
   * @param statusCode - any
   * @example
   */
  function renderStatusColor(statusCode: any) {
    switch (statusCode) {
      case 'New':
      case 'Open':
        return <span style={{ color: '#e5953d' }}>{statusCode}</span>;
      case 'Pending':
        return <span style={{ color: '#7aa0f8' }}>{statusCode}</span>;
      case 'Resolved':
        return <span style={{ color: '#4a7d44' }}>{statusCode}</span>;
      case 'Escalated':
        return <span style={{ color: '#a96ddb' }}>{statusCode}</span>;
      case 'Closed':
        return <span style={{ color: '#ed7354' }}>{statusCode}</span>;
      default:
        return '';
    }
  }

  /**
   * @example
   */
  function returnPreviousDate() {
    const date = new Date();

    date.setDate(date.getDate() - (props.index + 1 || 0));
    return date.toLocaleString('en-us', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
    // const day = date.getDate();
    // const month = date.getMonth() + 1;
    // const year = date.getFullYear();
    //return `${month}/${day}/${year}`;
  }

  /**
   * @example
   */
  function loadHistoryContact(cardInfo: any, correspondingDate: string) {
    
    console.log(cardInfo);
    if (
      cardInfo.digitalChannel === 'email' &&
      cardInfo.contactStatus !== 'Closed'
    ) {
      dispatch(
        updateContactInteraction({
          id: props?.interactionId,
          interaction: getInteractionData('HistoryEmail'),
        })
      );
      const dispatchedObj = {
        isEmailInteractionSelected: true,
        correspondingDate: correspondingDate,
      };
      dispatch(setEmailInteractionDates(dispatchedObj));
    }
  }

  return (
    <Card className={styles.cardDetails}>
      <CcfBox component="div" className={styles.details}>
        <CardContent
          classes={{
            root: styles.cardContent,
          }}
          className={styles.content}
        >
          <CcfBox component="div" className={styles.contentDetails}>
            <CcfBox component="div" className={styles.titleInfo}>
              {props && props.cardInfo && (
                <IconButton className={styles.titleLogo}>
                  {cardStatusIcon(props.cardInfo, 'left')}
                </IconButton>
              )}
              <CcfTypography className={styles.titleId}>
                {props?.cardInfo.contactId}
              </CcfTypography>
            </CcfBox>
            <CcfTypography className={styles.titleDesc}>
              {props?.cardInfo.contactRequest}
            </CcfTypography>
            <CcfBox component="div" className={styles.titleTag}>
              <CcfTypography className={styles.trackingId}>
                {props?.cardInfo.contactTrackingId !== ''
                  ? '#' + props?.cardInfo.contactTrackingId + ' | '
                  : ''}
              </CcfTypography>
              <CcfTypography className={styles.trackingId}>
                {props?.cardInfo.contactName}
              </CcfTypography>
            </CcfBox>
          </CcfBox>
        </CardContent>
      </CcfBox>
      <CcfBox component="div" className={styles.statusDivider}></CcfBox>
      <CcfBox
        component="div"
        className={
          props?.cardInfo.contactStatus === 'Open'
            ? styles.activeStatus
            : styles.inactiveStatus
        }
      >
        {props && props.cardInfo && (
          <IconButton
            className={
              props?.cardInfo.contactStatus === 'Open'
                ? styles.activeStatusLogo
                : styles.statusLogo
            }
            onClick={() =>
              loadHistoryContact(props.cardInfo, correspondingDate)
            }
          >
            {cardStatusIcon(props.cardInfo, 'right')}
          </IconButton>
        )}
        <CcfBox>
          <CcfTypography
            className={
              props?.cardInfo.contactStatus !== 'Open'
                ? styles.dateInfo
                : styles.hidddenDateInfo
            }
          >
            {(correspondingDate = returnPreviousDate())}
          </CcfTypography>

          <CcfTypography
            className={
              props?.cardInfo.contactStatus === 'Open'
                ? styles.activeStatusId
                : styles.inactiveStatusId
            }
          >
            {renderStatusColor(props?.cardInfo?.contactStatus)}
          </CcfTypography>
        </CcfBox>
      </CcfBox>
    </Card>
  );
}

export default CcfCustomerHistoryCard;
